// @ts-ignore
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import useAuthStore from "@shared-stores/auth.store";
import useStateStore from "@shared-stores/state.store";
import useLoadingState from "@shared-composables/useLoadingState";
import { isApp } from "@shared-utils/system.utils";

axios.defaults.baseURL = import.meta.env.VITE_API_BASE;
// Web/admin : envoie le cookie refresh HttpOnly en cross-origin (www ↔ api). Natif : sans effet.
axios.defaults.withCredentials = true;
axios.defaults.headers.common["Content-Type"] = "application/ld+json";
axios.defaults.timeout = 15000; // 15s max : évite qu'une requête stagnante (mobile) fige le skeleton
axios.defaults.headers.common["X-Source"] = "web";

import clientId from "@shared/client.id";
axios.defaults.headers.common["X-Client-Id"] = clientId;

// Flag pour éviter les refresh multiples simultanés
let isRefreshing = false;
// Queue des requêtes en attente pendant le refresh
let failedQueue: Array<{
	resolve: (token: string | null) => void;
	reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) =>
{
	failedQueue.forEach(prom =>
	{
		if (error)
		{
			prom.reject(error);
		}
		else
		{
			prom.resolve(token);
		}
	});
	failedQueue = [];
};

axios.interceptors.request.use(
	async function (config: InternalAxiosRequestConfig<any>)
	{
		const authStore = useAuthStore();
		const { loadingStore } = useLoadingState();
		const randomKey = (Math.random() + 1).toString(36).substring(7);
		let loadingKey = "";
		const url: string = config.url ?? "";
		const token = authStore.token;

		const skipLoading = config.data?.skipLoading === true;

		if (skipLoading)
		{
			delete config.data.skipLoading;
		}

		if (config.data?.loadingKey)
		{
			loadingKey = config.data.loadingKey;
		}
		else
		{
			loadingKey = randomKey;
			config.data = Object.assign(config.data ?? {}, { "loadingKey": randomKey });
		}

		if (!skipLoading)
		{
			loadingStore.enableLoading(loadingKey);
		}
		config.headers.setContentType("application/ld+json");
		config.headers.setAccept("application/ld+json");

		// Routes d'authentification - pas de token
		if (url.startsWith("auth") || url.startsWith("/auth") ||
			url.startsWith("token/refresh") || url.startsWith("/token/refresh"))
		{
			config.headers.setAuthorization("");
			return config;
		}

		// Routes account - pas de token
		if (url.startsWith("account") || url.startsWith("/account"))
		{
			config.headers.setAuthorization("");
			return config;
		}

		// Routes admin - token si valide
		if (url.startsWith("admin") || url.startsWith("/admin"))
		{
			if (authStore.isValidToken())
			{
				config.headers.setAuthorization(`Bearer ${ token }`);
			}
			else
			{
				config.headers.setAuthorization("");
			}
			return config;
		}

		// Routes public - token optionnel si valide
		if (url.startsWith("public/") || url.startsWith("/public/"))
		{
			if (authStore.isValidToken())
			{
				config.headers.setAuthorization(`Bearer ${ token }`);
			}
			else
			{
				config.headers.setAuthorization("");
			}
			return config;
		}

		// Routes user - token requis
		if (/^\/user(\/|$)/.test(url) || /^user(\/|$)/.test(url))
		{
			if (authStore.isValidToken())
			{
				config.headers.setAuthorization(`Bearer ${ token }`);
			}
			else
			{
				config.headers.setAuthorization("");
					// Rejet manuel avec erreur simulée 401
				return Promise.reject({
					response: {
						status: 401,
						statusText: "Unauthorized",
						data: { message: "Token invalide ou expiré" }
					},
					config
				});
			}
			return config;
		}

		// Routes stripe - token requis
		if (/^\/stripe(\/|$)/.test(url) || /^stripe(\/|$)/.test(url))
		{
			if (authStore.isValidToken())
			{
				config.headers.setAuthorization(`Bearer ${ token }`);
			}
			else
			{
				config.headers.setAuthorization("");
					// Rejet manuel avec erreur simulée 401
				return Promise.reject({
					response: {
						status: 401,
						statusText: "Unauthorized",
						data: { message: "Token invalide ou expiré" }
					},
					config
				});
			}
			return config;
		}

		// Autres routes - token si valide
		if (authStore.isValidToken())
		{
			config.headers.setAuthorization(`Bearer ${ token }`);
		}

		return config;
	},
	function (error: any)
	{
		return Promise.reject(error);
	}
);

axios.interceptors.response.use(
	function (response: AxiosResponse)
	{
		let loadingKey = "";
		const { loadingStore } = useLoadingState();

		if (typeof response.config?.data === "string")
		{
			const requestData = JSON.parse(response.config?.data ?? "{}");
			loadingKey = requestData.loadingKey;
		}
		else
		{
			loadingKey = response.config?.data?.loadingKey ?? "";
		}

		if (loadingKey !== "")
		{
			loadingStore.disableLoading(loadingKey);
		}
		else
		{
			loadingStore.resetStack();
		}

		return response;
	},
	async function (error: AxiosError)
	{
		const { loadingStore } = useLoadingState();
		const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
		const responseData: any = error?.response?.data ?? {};

		const errorCode = error?.response?.status ?? 0;
		const errorMessageServer = error?.response?.statusText ?? "";
		const errorMessage = responseData?.message ?? responseData ?? errorMessageServer;
		const url = originalRequest?.url ?? "";

		// Ne pas retry les requêtes d'auth ou de refresh
		const isAuthRequest = url.startsWith("auth") || url.startsWith("/auth") ||
			url.startsWith("token/refresh") || url.startsWith("/token/refresh") ||
			url.startsWith("account") || url.startsWith("/account");

		const authStore = useAuthStore();

		// Problème d'authentification - tenter un refresh. Uniquement si on a une session :
		// un visiteur anonyme (showcase /workouts non-connecté) n'a jamais eu de session → pas de
		// refresh, pas de logout/reset du store, pas de toast « Session expirée ».
		// Natif : signal = refresh token présent. Web/admin : signal = token en mémoire (le refresh
		// token vit dans le cookie HttpOnly, invisible au JS).
		const hasSession = isApp() ? !! authStore.refreshToken : !! authStore.token;

		if (errorCode === 401 && !originalRequest._retry && !isAuthRequest && hasSession)
		{
			// Marquer la requête comme déjà retentée
			originalRequest._retry = true;

			// Si un refresh est déjà en cours, mettre en queue
			if (isRefreshing)
			{
				return new Promise((resolve, reject) =>
				{
					failedQueue.push({ resolve, reject });
				}).then(token =>
				{
					if (token && originalRequest.headers)
					{
						originalRequest.headers["Authorization"] = `Bearer ${ token }`;
						return axios(originalRequest);
					}
					return Promise.reject(error);
				}).catch(err =>
				{
					return Promise.reject(err);
				});
			}

			isRefreshing = true;

			try
			{
				const newToken = await authStore.refreshAndGetNewToken();

				if (!newToken)
				{
					throw new Error("Token non récupéré");
				}

				authStore.parseAndSetToken(newToken);
				axios.defaults.headers.common["Authorization"] = "Bearer " + newToken;

				// Traiter la queue des requêtes en attente
				processQueue(null, newToken);

				// Relancer la requête originale
				if (originalRequest.headers)
				{
					originalRequest.headers["Authorization"] = `Bearer ${ newToken }`;
				}

				return axios(originalRequest);
			}
			catch (refreshError: any)
			{
				processQueue(refreshError, null);
				loadingStore.resetStack();

				// Le refresh n'a pas abouti pour une raison étrangère aux jetons (réseau coupé, 500) :
				// la session n'est pas en cause, on laisse l'adhérent connecté.
				const refreshStatus = refreshError?.response?.status ?? refreshError?.code;

				if (refreshStatus && refreshStatus !== 401 && refreshStatus !== 403)
				{
					return Promise.reject({
						code: errorCode,
						message: errorMessage
					});
				}

				// Refus d'authentification — déconnecter proprement
				authStore.referer = originalRequest?.url ?? "";
				authStore.logout();
				useStateStore().reset();

				return Promise.reject({
					code: 401,
					message: "Session expirée, veuillez vous reconnecter"
				});
			}
			finally
			{
				isRefreshing = false;
			}
		}

		loadingStore.resetStack();

		return Promise.reject({
			code: errorCode,
			message: errorMessage
		});
	}
);

// @ts-ignore — TS2303 « circular definition of import alias axios » : faux positif du TS service
// (cycle axios ↔ auth.store + ré-export du défaut axios). Le vue-tsc CLI passe. Cf. // @ts-ignore ligne 1.
// @ts-ignore
export default axios;
